import Fade from "@material-ui/core/Fade";
import { createStyles, makeStyles } from "@material-ui/styles";
import * as React from "react";

import { ErrorCard, FeedbackForm, RelodeTheme, Text, useRelodeApp } from "../";
import { getUserFromStorage, RelodeErrorBase } from "../../modules";
import { sendFeedback } from "../feedback/feedback.service";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100vh",
                [theme.breakpoints.down("sm")]: {
                    width: "100vw",
                },
            },
            container: {
                width: 700,
                height: 700,
                [theme.breakpoints.down("sm")]: {
                    width: "100%",
                    height: "100%",
                },
            },
            feedbackWrapper: {
                paddingTop: 16,
            },
            feedbackThanksWrapper: {
                display: "flex",
                position: "absolute",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                height: "100%",
                top: 0,
                right: 0,
                bottom: 0,
                left: 0,
            },
            summary: {
                display: "flex",
                justifyContent: "center",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                margin: "0 24px",
            },
        }),
    { name: "ErrorDisplayComponent" }
);

interface ErrorDisplayProps {
    error: RelodeErrorBase;
}

export const ErrorDisplay = (props: ErrorDisplayProps) => {
    const { error } = props;
    const classes = useStyles({});
    const feedbackSent = React.useRef(false);
    const {
        state: {
            config: { appName },
        },
    } = useRelodeApp();
    const user = getUserFromStorage();
    const username = !!user ? user.displayName : "unknown user";

    // force sending feedback if user clicks browser back button
    React.useEffect(() => {
        const handleUnload = async () => {
            await sendTheFeedback(
                "user refreshed browser without providing feedback"
            );
        };

        window.addEventListener("beforeunload", handleUnload);

        return () => {
            window.removeEventListener("beforeunload", handleUnload);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sendTheFeedback = async (comment?: string) => {
        if (feedbackSent.current) return;
        feedbackSent.current = true;
        try {
            const content = getFeedbackContent(comment);
            console.log("Error feedback", content);
            await sendFeedback(content);
        } catch (err) {
            console.log("Error occurred trying to send feedback", err);
        }
    };

    const getFeedbackContent = (comment?: string) => {
        const feedback = !!comment ? comment : "none provided";

        const content = `Unhandled error in ${appName} application\n\n
        User feedback: \n
        ${feedback}\n\n\n\n
        build number: ${appConfig.buildNumber}\n\n
        URL: ${window.location.pathname}${window.location.search}\n\n
        *********************\n
        Error Message:\n\n
        ${error.message}\n\n
        *********************\n
        Stack Trace:\n\n
        ${error.stack}\n\n
        *********************\n
        Telemetry Data:\n\n
        ${JSON.stringify(error.getTelemetryData(), null, 4)}
        `;
        return content;
    };
    const handleRefresh = async () => {
        await sendTheFeedback(
            "user clicked app refresh button without providing feedback"
        );
        window.location.href = `/${appName}`;
        window.location.reload();
    };

    const handleSendFeedback = async (comment: string) => {
        await sendTheFeedback(comment);
        window.location.href = `/${appName}`;
        window.location.reload();
    };

    const renderSummary = () => {
        if (process.env.NODE_ENV === "production") return null;

        return error?.message ? (
            <div className={classes.summary}>
                <Text variant="h4">{error?.message}</Text>
            </div>
        ) : null;
    };

    return (
        <div className={classes.root}>
            <div className={classes.container}>
                <ErrorCard
                    title="oops!"
                    subtitle="we have encountered something unexpected"
                    onClose={handleRefresh}
                    buttonTitle="refresh"
                    fullSize={true}
                >
                    {renderSummary()}
                    <div className={classes.feedbackWrapper}>
                        <Fade in={feedbackSent.current}>
                            <div className={classes.feedbackThanksWrapper}>
                                <Text variant="h2">thanks!</Text>
                            </div>
                        </Fade>
                        <Fade in={!feedbackSent.current}>
                            <div>
                                <FeedbackForm
                                    username={username}
                                    onSendFeedback={handleSendFeedback}
                                    // eslint-disable-next-line max-len
                                    placeholder="what were you doing before you landed here?  This will help us avoid these types of things in the future."
                                />
                            </div>
                        </Fade>
                    </div>
                </ErrorCard>
            </div>
        </div>
    );
};
